import { createBrowserRouter, Outlet, Navigate, RouterProvider, useRouteError } from 'react-router';
import AppBar from '@/components/app-bar';
import Chatbot from '@/components/chatbot';
import Home from '@/pages/home';
import Error from '@/pages/error';
import Video from '@/pages/video';
import DocVideo from '@/pages/doc-video';
import DocLabContent from '@/pages/doc-lab-content';
import DocMCP from '@/pages/doc-mcp';
import DocProduct from '@/pages/doc-product';
import AdminContent from '@/pages/admin-content';
import AdminSubCategory from './pages/admin-sub-category';
import AdminDocMCP from '@/pages/admin-doc-mcp';
import AdminDocProduct from '@/pages/admin-doc-product';
import AdminActiveSession from '@/pages/admin-active-session';
import AdminUserAccount from '@/pages/admin-user-account';
import AdminPermission from '@/pages/admin-permission';
import AdminBlacklist from '@/pages/admin-blacklist';
import AdminEula from '@/pages/admin-eula';
import AdminFileUploadEula from '@/pages/admin-file-upload-eula';
import AdminAiEula from '@/pages/admin-ai-eula';
import AdminEvent from '@/pages/admin-event';
import AdminLandingPage from '@/pages/admin-landing-page';
import AdminSwaggerFile from '@/pages/admin-swagger-file';
import AdminDeviceImage from '@/pages/admin-device-image';
import LabSchedule from '@/pages/lab-schedule';
import LabUserFile from '@/pages/lab-user-file';
import LabInteraction from '@/pages/lab-interaction';
import LabTerminal from '@/pages/lab-terminal';
import LabCraft from '@/pages/lab-craft';
import User from '@/pages/user';
import AdminDomain from '@/pages/admin-domain';
import AdminUserDomain from '@/pages/admin-user-domain';
import AdminSchedule from '@/pages/admin-schedule';
import AdminReportIssue from '@/pages/admin-report-issue';
import AdminLogMonitor from '@/pages/admin-log-monitor';
import InstructorClassroom from '@/pages/instructor-classroom';
import InstructorStudent from '@/pages/instructor-student';
import InstructorDashboard from '@/pages/instructor-dashboard';
import AnalyticsGenerateReport from '@/pages/analytics-generate-report';
import MediaDocVideo from '@/pages/media-doc-video';
import MediaVideoNotification from '@/pages/media-video-notification';
import Footer from '@/components/footer';
import { CEC, ROUTES } from '@/constants';
import RouteListener from '@/components/route-listener';
import AuthGuard from '@/components/auth-guard';

const router = createBrowserRouter([
  {
    ErrorBoundary: () => {
      const error = useRouteError();
      const { pathname, search } = window.location;
      const message = error?.message || 'Something went wrong';
      const details = `Path: ${pathname}${search} - ${error?.name || 'Error'}: ${message}`;

      return (
        <Navigate
          to={`${ROUTES.ERROR}?R=${encodeURIComponent(details)}`}
          replace
        />
      );
    },
    element: (
      <div id="body">
        <AppBar />
        <div style={{ display: 'flex' }}>
          <Chatbot />
          <div style={{ flex: 1 }}>
            <RouteListener />
            <Outlet />
            <Footer />
          </div>
        </div>
      </div>
    ),
    children: [
      // Public
      { path: CEC, element: <Home /> },
      { path: ROUTES.HOME, element: <Home /> },
      { path: ROUTES.ERROR, element: <Error /> },
      { path: `${ROUTES.DOC_VIDEO}/:referenceId`, element: <DocVideo /> },
      // Auth required
      {
        element: <AuthGuard />,
        children: [
          // Docs
          { path: ROUTES.DOCLAB_CONTENT, element: <DocLabContent /> },
          { path: ROUTES.DOCMCP, element: <DocMCP /> },
          { path: ROUTES.DOCPRODUCT, element: <DocProduct /> },
          { path: ROUTES.VIDEO, element: <Video /> },
          // Admin - Content Management
          { path: ROUTES.ADMIN_CONTENT, element: <AdminContent /> },
          { path: ROUTES.ADMIN_SUBCATEGORY, element: <AdminSubCategory /> },
          { path: ROUTES.ADMIN_DOCMCP, element: <AdminDocMCP /> },
          { path: ROUTES.ADMIN_DOCPRODUCT, element: <AdminDocProduct /> },
          // Admin - Security
          { path: ROUTES.ADMIN_ACTIVESESSION, element: <AdminActiveSession /> },
          { path: ROUTES.ADMIN_USERACCOUNT, element: <AdminUserAccount /> },
          { path: ROUTES.ADMIN_PERMISSION, element: <AdminPermission /> },
          { path: ROUTES.ADMIN_BLACKLIST, element: <AdminBlacklist /> },
          // Admin - Domain Management
          { path: ROUTES.ADMIN_DOMAIN, element: <AdminDomain /> },
          { path: ROUTES.ADMIN_USERDOMAIN, element: <AdminUserDomain /> },
          // Admin - Site Management
          { path: ROUTES.ADMIN_EULA, element: <AdminEula /> },
          { path: ROUTES.ADMIN_FILEUPLOADEULA, element: <AdminFileUploadEula /> },
          { path: ROUTES.ADMIN_AIEULA, element: <AdminAiEula /> },
          { path: ROUTES.ADMIN_EVENT, element: <AdminEvent /> },
          { path: ROUTES.ADMIN_LANDINGPAGE, element: <AdminLandingPage /> },
          { path: ROUTES.ADMIN_SWAGGERFILE, element: <AdminSwaggerFile /> },
          { path: ROUTES.ADMIN_DEVICEIMAGE, element: <AdminDeviceImage /> },
          // Admin - Schedule Management
          { path: ROUTES.ADMIN_SCHEDULE, element: <AdminSchedule /> },
          // Admin - Report Issue Management
          { path: ROUTES.ADMIN_REPORTISSUE, element: <AdminReportIssue /> },
          // Admin - Log Monitor Management
          { path: ROUTES.ADMIN_LOGMONITOR, element: <AdminLogMonitor /> },
          // Lab
          { path: ROUTES.LAB_SCHEDULE, element: <LabSchedule /> },
          { path: ROUTES.LAB_USERFILE, element: <LabUserFile /> },
          { path: `${ROUTES.LAB_INTERACTION}/:bookingId`, element: <LabInteraction /> },
          { path: `${ROUTES.LAB_TERMINAL}/:webTermLink`, element: <LabTerminal /> },
          { path: ROUTES.LAB_CRAFT, element: <LabCraft /> },
          // Instructor
          { path: ROUTES.INSTRUCTOR_CLASSROOM, element: <InstructorClassroom /> },
          { path: ROUTES.INSTRUCTOR_STUDENT, element: <InstructorStudent /> },
          { path: ROUTES.INSTRUCTOR_DASHBOARD, element: <InstructorDashboard /> },
          // Analytics
          { path: ROUTES.ANALYTICS_GENERATE_REPORT, element: <AnalyticsGenerateReport /> },
          // Media
          { path: ROUTES.MEDIA_DOC_VIDEO, element: <MediaDocVideo /> },
          { path: ROUTES.MEDIA_VIDEO_NOTIFICATION, element: <MediaVideoNotification /> },
          // User Profile
          { path: ROUTES.USER, element: <User /> },
          // Redirect
          {
            path: ROUTES.LAB,
            element: (
              <Navigate
                to={ROUTES.LAB_SCHEDULE}
                replace
              />
            ),
          },
          {
            path: '*',
            element: (
              <Navigate
                to={ROUTES.HOME}
                replace
              />
            ),
          },
        ],
      },
    ],
  },
]);

const RouterConfig = <RouterProvider router={router} />;

export default RouterConfig;
